import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton, InputAdornment, Stack, Box, Avatar } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import Tooltip from '@mui/material/Tooltip';
import ClearIcon from '@mui/icons-material/Clear';
import Divider from '@mui/material/Divider';
import FactoryIcon from '@mui/icons-material/Factory';
import { FilePond, registerPlugin } from 'react-filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';

import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import ExpandableTextArea from 'components/ExpandableTextArea';
import CodeInput from 'components/inputs/CodeInput';
import { buildImageUrl } from 'services/imageResourceApi';

registerPlugin(
  FilePondPluginFileValidateType,
  FilePondPluginImageExifOrientation,
  FilePondPluginImagePreview
);

/**
 * Component for displaying and managing AI provider details
 * Allows viewing, creating, and updating providers
 */
function AiProviderDetails({ provider, onSubmit = () => {}, onFieldChange = () => {} }) {
  const { formatMessage: msg } = useIntl();
  const [svgFiles, setSvgFiles] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty }
  } = useForm({
    defaultValues: {
      id: '',
      code: '',
      label: '',
      displayableKey: '',
      url: '',
      description: '',
      svgIcon: null
    }
  });

  watch();

  useEffect(() => {
    if (onFieldChange) {
      onFieldChange(isDirty);
    }
  }, [isDirty, onFieldChange]);

  const resetProvider = useCallback(() => {
    if (provider?.code) {
      reset(provider);
      setSvgFiles([]);
    } else {
      reset({
        id: '',
        code: '',
        label: '',
        displayableKey: '',
        url: '',
        description: '',
        svgIcon: null
      });
      setSvgFiles([]);
    }
  }, [provider, reset]);

  useEffect(() => {
    resetProvider();
  }, [provider, resetProvider]);

  const clearField = (fieldName) => {
    setValue(fieldName, '');
  };

  const handleReset = () => {
    resetProvider();
  };

  const handleSvgUpload = (fileItems) => {
    setSvgFiles(fileItems);

    if (fileItems.length > 0) {
      const file = fileItems[0].file;
      setValue('svgIcon', file);
      onFieldChange && onFieldChange(true);
    } else {
      setValue('svgIcon', null);
    }
  };

  const handleFormSubmit = (data) => {
    const formData = { ...data };

    if (svgFiles.length > 0) {
      formData.svgIconFile = svgFiles[0].file;
    }

    onSubmit(formData);
  };

  const isCreating = !provider?.id;
  const imageUrl = buildImageUrl(provider?.code, 'aiproviders');

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(handleFormSubmit)}
      noValidate
      display="flex"
      direction="column"
      sx={{ height: '100%' }}
    >
      <Stack display="flex" direction="row">
        <FactoryIcon color="text" fontSize="medium" sx={{ mr: '0.75rem' }} />
        <MDTypography variant="h5">
          {msg({
            id: isCreating
              ? 'page.aiProviders.details.creation.title'
              : 'page.aiProviders.details.modification.title'
          })}
        </MDTypography>
      </Stack>
      <Divider sx={{ mt: '0.5rem', mb: '0.75rem' }} />
      <Stack
        spacing={1}
        display="flex"
        direction="column"
        flex={1}
        overflow="auto"
        mb={2}
        sx={{ scrollbarWidth: 'thin' }}
      >
        {!isCreating && (
          <Controller
            name="id"
            control={control}
            render={({ field }) => (
              <MDInput
                {...field}
                label={msg({ id: 'field.id.label' })}
                variant="filled"
                fullWidth
                required
                size="small"
                InputProps={{
                  readOnly: true
                }}
              />
            )}
          />
        )}
        <Controller
          name="code"
          control={control}
          rules={{ required: msg({ id: 'field.code.required' }) }}
          render={({ field }) => (
            <CodeInput field={field} errors={errors} clearField={() => clearField('code')} />
          )}
        />
        <Controller
          name="label"
          control={control}
          rules={{ required: msg({ id: 'field.label.required' }) }}
          render={({ field }) => (
            <MDInput
              {...field}
              label={msg({ id: 'field.label.label' })}
              variant="filled"
              fullWidth
              required
              size="small"
              error={!!errors.label}
              helperText={errors.label ? errors.label.message : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('label')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <Controller
          name="displayableKey"
          control={control}
          render={({ field }) => (
            <MDInput
              {...field}
              label={msg({ id: 'field.apiKey.label' })}
              variant="filled"
              fullWidth
              size="small"
              error={!!errors.displayableKey}
              helperText={errors.displayableKey ? errors.apiKey.message : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('displayableKey')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <Controller
          name="url"
          control={control}
          rules={{ required: msg({ id: 'field.url.required' }) }}
          render={({ field }) => (
            <MDInput
              {...field}
              label={msg({ id: 'field.url.label' })}
              variant="filled"
              fullWidth
              required
              size="small"
              error={!!errors.url}
              helperText={errors.url ? errors.url.message : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('url')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <MDTypography variant="subtitle2" gutterBottom fontWeight={'bold'}>
            {msg({ id: 'field.svgIcon.label' }) || 'Provider Icon (SVG)'}
          </MDTypography>

          <Box sx={{ display: 'flex' }}>
            <Avatar
              src={imageUrl}
              alt={provider?.code}
              variant="rounded"
              sx={{ width: '76px', height: '76px', mr: 1 }}
            >
              AI
            </Avatar>
            <Controller
              name="svgIcon"
              control={control}
              render={() => (
                <FilePond
                  files={svgFiles}
                  onupdatefiles={handleSvgUpload}
                  acceptedFileTypes={['image/svg+xml']}
                  fileValidateTypeDetectType={(source, type) => {
                    if (typeof source === 'string') {
                      return null;
                    }
                    if (source.name && source.name.toLowerCase().endsWith('.svg')) {
                      return Promise.resolve('image/svg+xml');
                    }
                    return Promise.resolve(type);
                  }}
                  allowMultiple={false}
                  maxFiles={1}
                  name="svgIcon"
                  labelIdle={msg({ id: 'field.svgIcon.upload.description' })}
                />
              )}
            />
          </Box>
        </Box>
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <ExpandableTextArea
              field={field}
              error={!!errors.description}
              helperText={errors.description ? errors.description.message : ''}
              clearField={() => clearField('description')}
              label={msg({ id: 'field.description.label' })}
              control={control}
            />
          )}
        />
      </Stack>
      <Divider sx={{ mt: 0 }} />
      <Stack direction="row" spacing={2} sx={{ marginTop: 'auto !important' }}>
        <MDButton type="button" fullWidth color="secondary" onClick={handleReset}>
          {msg({ id: 'button.reset' })}
        </MDButton>
        <MDButton type="submit" fullWidth color="primary">
          {isCreating ? msg({ id: 'button.create' }) : msg({ id: 'button.update' })}
        </MDButton>
      </Stack>
    </Stack>
  );
}

AiProviderDetails.propTypes = {
  provider: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string,
    name: PropTypes.string,
    apiKey: PropTypes.string,
    endpoint: PropTypes.string,
    description: PropTypes.string
  }),
  onSubmit: PropTypes.func,
  onFieldChange: PropTypes.func
};

export default AiProviderDetails;
